import { MemberInterface } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { FC, useEffect, useState } from "react";

import MembersModel from "../../../../../../Models/Member/Members.Model";
import ModalModel from "../../../../../../Models/Modal/Modal.Model";
import { CheckboxesModal } from "./CheckboxesModal";
import { MembersList } from "./MembersList";
import { getAgentName } from "./MembersList.Helpers";

interface Props {
  selectedMemberIds: number[];
  onMembersChange: (memberId: number[]) => void;
}

const mergeArrays = (arr1: number[], arr2: number[]) => {
  const set1 = new Set(arr1);
  const uniqueElementsFromArr2 = arr2.filter((item) => !set1.has(item));
  return [...arr1, ...uniqueElementsFromArr2];
};

export const MembersSelector: FC<Props> = ({ selectedMemberIds, onMembersChange }) => {
  const [selectedMembers, setSelectedMembers] = useState<MemberInterface[]>([]);

  useEffect(() => {
    if (selectedMemberIds.length) {
      (async () => {
        // Loading members to the global state if they are not loaded yet
        if (!MembersModel.state.members?.length) {
          await MembersModel.loadMembers();
        }

        // Collecting a list of selected members
        const selectedMembers = selectedMemberIds.reduce((acc, memberId) => {
          const member = MembersModel.state.members?.find((member) => member.id === memberId);
          if (member) {
            acc.push(member);
          }
          return acc;
        }, [] as MemberInterface[]);

        setSelectedMembers(selectedMembers);
      })();
    } else {
      setSelectedMembers([]);
    }
  }, [selectedMemberIds]);

  const onReorder = (membersOrder: MemberInterface[]) => {
    onMembersChange(membersOrder.map((m) => m.id));
  };

  const onAddMembers = (addedMemberIds: number[]) => {
    const keptMemberIds = selectedMembers.filter((m) => addedMemberIds.includes(m.id)).map((m) => m.id);
    onMembersChange(mergeArrays(keptMemberIds, addedMemberIds));
    ModalModel.hideModal();
  };

  const onDelete = (memberId: number) => {
    const keptMemberIds = selectedMembers.filter((m) => m.id !== memberId).map((m) => m.id);
    onMembersChange(keptMemberIds);
  };

  const addMemberClickHandler = () => {
    const options = MembersModel.state.members!.map((member) => {
      return {
        label: getAgentName(member),
        value: member.id
      };
    });
    ModalModel.showModalWithContent(
      <>Add Members to Queue</>,
      <CheckboxesModal
        value={selectedMemberIds}
        options={options}
        onSubmit={onAddMembers}
        onCancel={() => ModalModel.hideModal()}
        cancelTitle="Cancel"
        submitTitle="Add Members"
      />
    );
  };

  return (
    <div>
      <MembersList onReorder={onReorder} onDelete={onDelete} members={selectedMembers} />
      <Button variant={ButtonVariant.DASHED} size={ButtonSize.LARGE} onClick={addMemberClickHandler}>
        + Add Members
      </Button>
    </div>
  );
};
